import React, {FC, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {useParams} from "react-router";
import {Box, Checkbox, Flex, Text, Tooltip, useBreakpointValue, useToast} from "@chakra-ui/react";
import {useAppSelector} from "../../redux/Store";
import {Product} from "../../shared/entities/Product/Product";
import {ListProductItemConfig, ListProductItemRow} from "../../components/List/ListProductItem";
import {Loader} from "../../components/Loader/Loader";
import {MainButton} from "../../components/Buttons/MainButton";
import {BoxStyleConfig} from "../Forms/FormStyleConfigs";
import {setBreadcrumbsLinks} from "../../redux/Breadcrumbs/BreadcrumbsReducer";
import {Breadcrumbs} from "../../components/Breadcrumbs/Breadcrumbs";
import ImageCustom from "../../components/Image/ImageCustom";
import QuantityInput from "../../components/Inputs/QuantityInput";
import {ProductTypes} from "../../common/constants";
import DiscountComponent from "../Products/Discount/DiscountComponent";
import {ProductController} from "../../controllers/ProductController";
import {CartService} from "../../service/Cart/CartService";
import {BreadcrumbsData} from "../../shared/entities/Breadcrumbs/BreadCrumbsData";
import {OrderProductController} from "../../controllers/OrderProductController";
import {UserController} from "../../controllers/UserController";
import {ProductCostCalculateUtils} from "../../shared/utilities/ProductCostCalculateUtils";
import {hasPermission, isRoleAdmin} from "../../features/Permission/AllowedAccess";
import {scheduledShipmentCreatePermission, scheduledShipmentEditPermission} from "../permissions";
import AddScheduledShipmentPopover from "../Modal/AddScheduledShipmentPopover";
import {ScheduledShipmentController} from "../../controllers/ScheduledShipmentController";
import {
    mapShipments,
    optionReorderValues, optionReorderWeeklyValues,
    ScheduledShipment
} from "../../shared/entities/Order/ScheduledShipment";
import SchedulerMonths from "../Modal/SchedulerMonths";
import {ColumnConfig} from "../../components/Buttons/ColumnHandler";
import OrderDealershipPopover from "../Modal/OrderDealershipPopover";
import {parse} from "date-fns";

export const OrderBasket: FC<{ account?: boolean, links?: Array<BreadcrumbsData> }> = ({account = false, links}) => {
    const dispatch = useDispatch();

    const toast = useToast()
    const [isLoading, setIsLoading] = useState(true)
    const orderProductController = new OrderProductController(dispatch);
    const productController = new ProductController(dispatch);
    const {dealerId} = useParams();
    const userController = new UserController(dispatch)
    const cartService = new CartService(dispatch)

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [updatedProductList, setProductList] = useState<Array<Product>>([]);

    const cartItems = useAppSelector((state) => state.CartReducer.cartItems);
    const authDealership = useAppSelector(state => state.AuthReducer.dealership);
    const selDealership = useAppSelector(state => state.UserReducer.dealership);
    const scheduledShipmentController = new ScheduledShipmentController(dispatch);
    const scheduledList = useAppSelector(state => state.ScheduledShipmentReducer.list);
    const [refresh, setRefresh] = useState(false);

    let isMobile = useBreakpointValue({base: true, sm: true, md: true, lg: false, xl: false, "2xl": false})
    if (links) {
        setBreadcrumbsLinks(dispatch, links);
    }

    useEffect(() => {
        setIsLoading(true);
        scheduledShipmentController.clearListShipment();
        let id = dealerId ? dealerId : authDealership?.id
        if (id) {
            scheduledShipmentController.all({data: {dealershipId: id, isAdmin: isRoleAdmin()}})
            userController.getDealership({id: Number(id)});
            orderProductController.getAllProductsFromDealerOrders(Number(id)).then((orderProducts) => {
                setIsLoading(false);
                if (orderProducts.None) {
                    toast({
                        position: "top",
                        title: 'Error receiving order,' + orderProducts?.None?.getErrorDescription() + ', try logging in',
                        status: 'error',
                        duration: 8000,
                        isClosable: true,
                    })
                } else {
                    const newUpdatedProductList = orderProducts.Some.map((elem: any) => {
                        return {
                            ...elem.product,
                            quantity: 0,
                            discount: elem.product.discount,
                            shippingCost: elem.product.shippingCost,
                            taxCost: elem.product.taxCost,
                            price: elem.product.price,
                            finalPrice: elem.product.price,
                            orders: elem.orders,
                            averageOrders: elem.averageOrders
                        };
                    });
                    setProductList(newUpdatedProductList);
                }
            });
        } else {
            setIsLoading(false);
        }
    }, [dealerId, authDealership, refresh]);

    const columns: Array<ColumnConfig> = isMobile ? [
        {
            header: <Box>
                <Text>Product</Text>
                <Text>Item price</Text>
            </Box>,
            width: "30%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        }, {
            header: <Box>
                <Text>Info</Text>
                <Text>Qty</Text>
            </Box>,
            width: "40%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        }, {
            header: <Box>
                <Text>Total price</Text>
            </Box>,
            width: "30%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        }
    ] : [
        {
            header: "Product",
            width: "10%",
        },
        (hasPermission(scheduledShipmentCreatePermission) || hasPermission(scheduledShipmentEditPermission)) && {
            header: "Subscription",
            width: "25%",
        },
        {
            header: "Info",
            width: (hasPermission(scheduledShipmentCreatePermission) || hasPermission(scheduledShipmentEditPermission)) ? "27%" : "47%",
        },
        {
            header: "Item price",
            width: "10%",
        },
        {
            header: "Qty",
            width: "8%",
        },
        {
            header: "Total price",
            width: "7%",
        },
        {
            header: "Purchase history",
            width: "10%",
        },
        {
            header: "",
            width: "3%",
        }
    ].filter(Boolean) as Array<ColumnConfig>;

    const headers: Array<ListProductItemConfig> = columns.map(
        (col: ColumnConfig) => {
            return {
                content: col.header || "",
                width: col.width || "auto",
                isLink: col.isLink || false,
                style: col.style || {},
            };
        }
    );
    const handleCheckboxChange = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
        }
    };

    const handleAddedSelectProductToCart = () => {
        setIsLoading(true);
        productController.getProductsIds({ids: selectedIds?.join(",")}).then(productList => {
            if (productList.Some) {
                productList.Some.items.map((product: any) => {
                    const existingProduct = updatedProductList.find((item) => item.id === product.id);
                    if (existingProduct) {
                        product.quantity = existingProduct.quantity;
                    }
                    cartService.addProductToCart(product);
                })
                setIsLoading(false);
            }
        });
    }
    const removeIdFromSelection = (id: number) => {
        setSelectedIds(prev => prev.filter(itemId => itemId !== id));
    };

    const getList = (productList: Array<Product>) => {
        let shipmentMap: Map<number, ScheduledShipment> | undefined;
        if (scheduledList.items) {
            shipmentMap = mapShipments(scheduledList.items);
        }
        return productList?.map((elem: Product, idx: number) => {
            const shipment = shipmentMap?.get(elem.id);
            const ItemConfig: Array<ListProductItemConfig> = isMobile ? [
                    {
                        content:
                            <Box pb={4} width={"100%"}>
                                <Flex>
                                    <Box minWidth={"25%"}>
                                        <ImageCustom elem={elem} sizeInPercent={100} currentSize={"medium"}
                                                     sizeBackend={"COMMON"}/>
                                    </Box>

                                    <Box flexDir="column" textAlign="left" pl={3} pt={2}>
                                        <Text fontSize={16} fontWeight="md">{elem.name}</Text>
                                        {/*<Text fontWeight="500" color="gray.400"> Product#: {elem.id} </Text>*/}
                                        <Text fontSize={14} color="gray.400">{elem.description}</Text>
                                        {elem.type === ProductTypes.CUSTOM && (
                                            <Text fontWeight="600" fontSize="16px">
                                                {'This product is custom and will be billed and shipped on separate invoice.'}
                                            </Text>
                                        )}
                                        {elem.type === ProductTypes.REORDER && (
                                            <Text fontWeight="600" fontSize="16px">
                                                {'This product will be billed and shipped on separate invoice.'}
                                            </Text>
                                        )}
                                        {(hasPermission(scheduledShipmentCreatePermission) || hasPermission(scheduledShipmentEditPermission)) &&
                                            <>
                                                <Flex>
                                                    {!elem.subscriptionExcluded &&
                                                        <AddScheduledShipmentPopover
                                                            isDisabledSelDealer={true}
                                                            onSuccess={() => setRefresh(prevIndex => !prevIndex)}
                                                            item={shipment ? {
                                                                ...shipment.product,
                                                                quantity: shipment.quantity
                                                            } : elem}
                                                            editItem={shipment ? shipment : undefined}
                                                            dealer={{
                                                                id: selDealership?.id ?? 0,
                                                                name: selDealership?.name ?? "Unknown"
                                                            }}
                                                        />
                                                    }
                                                    {(shipment && !elem.subscriptionExcluded) &&
                                                        <Flex fontSize={14} wrap="nowrap" align="center" pl={1}>
                                                            <Text color="gray.600" whiteSpace="nowrap">
                                                                Qnt: <b>{shipment.quantity}</b>
                                                            </Text>
                                                            <Text color="gray.600" pl={2} whiteSpace="nowrap">
                                                                Every: <b>{(elem?.subscriptionWeekly ? optionReorderWeeklyValues : optionReorderValues).find(v => v.value === shipment.reorderPeriod)?.label}</b>
                                                            </Text>
                                                            {/*<Text color="gray.600" pl={2} whiteSpace="nowrap">*/}
                                                            {/*    Next Start: <b>{shipment.nextStartDate}</b>*/}
                                                            {/*</Text>*/}
                                                        </Flex>
                                                    }
                                                </Flex>
                                                {(shipment && !elem.subscriptionExcluded && !elem?.subscriptionWeekly) &&
                                                    <SchedulerMonths frequency={shipment.reorderPeriod} popover={false}
                                                                     startDate={parse(shipment.nextStartDate, 'MM.dd.yyyy', new Date())}
                                                    />
                                                }
                                            </>
                                        }
                                    </Box>
                                </Flex>
                                <Flex alignItems={"center"} pt={2}>
                                    <Flex minWidth={"32%"}>
                                        <DiscountComponent elem={elem} hiddenUOM={true}
                                                           fontWeightPrice={"300"}
                                                           textAlign={"left"}
                                                           selectedDealer={selDealership}
                                        />
                                    </Flex>
                                    <Flex w={"100%"}>
                                        <QuantityInput item={elem}
                                                       onQuantityRemove={() => removeIdFromSelection(elem.id)}
                                                       isMobile={isMobile}
                                                       width={"50%"}
                                                       onQuantityChange={(updatedItem) => {
                                                           const updatedProduct = cartItems.find((item) => item.id === elem.id);
                                                           setProductList((prevProductList) => {
                                                               return prevProductList.map((item) => {
                                                                   if (item.id === updatedItem.id) {
                                                                       return {...item, quantity: updatedItem.quantity}; // Создаём новый объект с обновлённым quantity
                                                                   }
                                                                   return item;
                                                               });
                                                           });
                                                           if (updatedProduct) {
                                                               updatedProduct.quantity = updatedItem.quantity;
                                                               cartService.addProductToCart(updatedProduct);
                                                           }
                                                       }}/>
                                        <Flex justifyContent={"space-between"} w={"50%"} alignItems={"center"}>
                                            <Text fontSize="16px" fontWeight={"md"}
                                                  pl={3}> {"$" + (elem.price * elem.quantity).toFixed(2)}
                                            </Text>

                                            <Box pr={1}>
                                                <Checkbox marginLeft={"10px"}
                                                          isDisabled={!elem.quantity || elem.quantity <= 0}
                                                          checked={selectedIds.includes(elem?.id)}
                                                          onChange={(event) => handleCheckboxChange(elem?.id, event.target.checked)}
                                                />
                                            </Box>
                                        </Flex>
                                    </Flex>
                                </Flex>
                                <Flex pt={2}>
                                    {elem.orders && elem.orders.length > 0 ?
                                        <Flex flexDir={"column"} w={"100%"}>
                                            <OrderDealershipPopover items={elem.orders} product={elem}/>
                                            {elem.averageOrders && Math.ceil(elem.averageOrders) > 0 && (
                                                <Text fontSize={12} color="gray.500" mt={1} ml={"auto"} fontWeight={"md"}>
                                                    ≈ {Math.ceil(elem.averageOrders)} per month
                                                </Text>
                                            )}
                                        </Flex> :
                                        <Text fontSize={12}>{"---"}</Text>
                                    }
                                </Flex>
                            </Box>,
                        isLink: false,
                        width: "100%",
                        justifyContent: "flex-start"
                    }] :
                [
                    {
                        content: <ImageCustom elem={elem} sizeInPercent={100} currentSize={"medium"}
                                              sizeBackend={"COMMON"}/>,
                        isLink: false,
                        width: columns[0]?.width || "auto",
                    },
                    (hasPermission(scheduledShipmentCreatePermission) || hasPermission(scheduledShipmentEditPermission)) && {
                        content: <Box w={"100%"}>
                            <Flex>
                                {!elem.subscriptionExcluded &&
                                    <AddScheduledShipmentPopover
                                        isDisabledSelDealer={true}
                                        onSuccess={() => setRefresh(prevIndex => !prevIndex)}
                                        item={shipment ? {...shipment.product, quantity: shipment.quantity} : elem}
                                        editItem={shipment ? shipment : undefined}
                                        dealer={{
                                            id: selDealership?.id ?? 0,
                                            name: selDealership?.name ?? "Unknown"
                                        }}
                                    />
                                }
                                {(shipment && !elem.subscriptionExcluded) &&
                                    <Flex fontSize={14} wrap="nowrap" align="center" pl={1}>
                                        <Text color="gray.600" whiteSpace="nowrap">
                                            Qnt: <b>{shipment.quantity}</b>
                                        </Text>
                                        <Text color="gray.600" pl={2} whiteSpace="nowrap">
                                            Every: <b>{(elem?.subscriptionWeekly ? optionReorderWeeklyValues : optionReorderValues).find(v => v.value === shipment.reorderPeriod)?.label}</b>
                                        </Text>
                                        {/*<Text color="gray.600" pl={2} whiteSpace="nowrap">*/}
                                        {/*    Next Start: <b>{shipment.nextStartDate}</b>*/}
                                        {/*</Text>*/}
                                    </Flex>
                                }
                            </Flex>
                            {(shipment && !elem.subscriptionExcluded && !elem?.subscriptionWeekly) &&
                                <SchedulerMonths frequency={shipment.reorderPeriod} popover={false}
                                                 startDate={parse(shipment.nextStartDate, 'MM.dd.yyyy', new Date())}
                                />
                            }
                        </Box>,
                    },
                    {
                        content:
                            <Flex flexDir="column" textAlign="left">
                                <Text fontSize={16} fontWeight="md">{elem.name}</Text>
                                {/*<Text fontWeight="500" color="gray.400"> Product#: {elem.id} </Text>*/}
                                <Text fontSize={14} color="gray.400">{elem.description}</Text>
                                {elem.type === ProductTypes.CUSTOM && (
                                    <Text fontWeight="600" fontSize="16px">
                                        {'This product is custom and will be billed and shipped on separate invoice.'}
                                    </Text>
                                )}
                                {elem.type === ProductTypes.REORDER && (
                                    <Text fontWeight="600" fontSize="16px">
                                        {'This product will be billed and shipped on separate invoice.'}
                                    </Text>
                                )}
                            </Flex>
                        ,
                    },
                    {
                        content: <DiscountComponent elem={elem} hiddenUOM={true} selectedDealer={selDealership}
                                                    textAlign={"left"}
                                                    showMarkup={true} showDiscount={true} showTax={true}/>,
                    },
                    {
                        content:
                            <QuantityInput item={elem}
                                           onQuantityRemove={() => removeIdFromSelection(elem.id)}
                                           onQuantityChange={(updatedItem) => {
                                               const updatedProduct = cartItems.find((item) => item.id === elem.id);
                                               setProductList((prevProductList) => {
                                                   return prevProductList.map((item) => {
                                                       if (item.id === updatedItem.id) {
                                                           return {...item, quantity: updatedItem.quantity}; // Создаём новый объект с обновлённым quantity
                                                       }
                                                       return item;
                                                   });
                                               });
                                               if (updatedProduct) {
                                                   updatedProduct.quantity = updatedItem.quantity;
                                                   cartService.addProductToCart(updatedProduct);
                                               }
                                           }}/>,
                    },
                    {
                        content: <Text fontSize={14}
                                       userSelect="none"> {"$" + Number((ProductCostCalculateUtils.calculatePrice(elem, selDealership) * elem.quantity).toFixed(2))} </Text>,
                    },
                    {
                        content: elem.orders && elem.orders.length > 0 ? (
                            <Box>
                                <OrderDealershipPopover items={elem.orders} product={elem}/>
                                {elem.averageOrders && Math.ceil(elem.averageOrders) > 0 && (
                                    <Text fontSize={12} color="gray.500" mt={1} fontWeight={"md"}>
                                        ≈ {Math.ceil(elem.averageOrders)} per month
                                    </Text>
                                )}
                            </Box>
                        ) : (
                            <Text fontSize={12}>{"---"}</Text>
                        ),
                    },
                    {
                        content: (
                            <Tooltip
                                label="To activate, quantity must be greater than zero"
                                isDisabled={elem.quantity > 0}
                                placement="top"
                                hasArrow
                            >
                                <Box>
                                    <Checkbox
                                        isChecked={selectedIds.includes(elem?.id)}
                                        onChange={(event) => handleCheckboxChange(elem?.id, event.target.checked)}
                                        isDisabled={!elem.quantity || elem.quantity <= 0}
                                    />
                                </Box>
                            </Tooltip>
                        )
                        ,
                    },
                ].filter(Boolean) as Array<ListProductItemConfig>;
            return <ListProductItemRow key={idx} list={ItemConfig} style={{justifyContent: "flex-start"}}
                                       headers={columns}/>;
        });
    }

    return (
        <>
            {!account &&
                <Breadcrumbs/>
            }
            {isLoading ? <Loader/> :
                <Box height={"100%"} p={{base: 0, lg: 0}}>
                    {/*<CustomStepper/>*/}
                    <Box {...BoxStyleConfig} pt={2}>
                        <ListProductItemRow list={headers} noBorder={false}
                                            style={{fontSize: 14, justifyContent: "flex-start"}}/>
                        {getList(updatedProductList)}
                        <MainButton ml={{lg: "auto"}} minWidth={"180px"} minH={"40px"} mt={4}
                                    disabled={selectedIds && selectedIds.length === 0}
                                    onClick={() => handleAddedSelectProductToCart()}>
                            {'Add to cart'}
                        </MainButton>
                    </Box>
                </Box>
            }
        </>
    );
}

export default OrderBasket;
